/**
 * Embedded Excel charts on a live Univer sheet — shared by the SDMS Template Builder design
 * sheet and SDMS Result Entry (owner reports 2026-10-12: the Windows builder / ALPACA show the
 * calibration chart, the web did not; "if I change value chart should react").
 *
 * Univer cannot render an embedded Excel chart, so each chart is rebuilt with Recharts
 * (SdmsChartPreview) from what the chart part itself says — readChartSeries: the X / Y ranges,
 * value-axis title, legend, markers — and floated over the cells its anchor spans
 * (readChartAnchors + anchorToBox) through the drawing preset's range-anchored float DOM, so it
 * scrolls with the sheet. The values are re-read from the LIVE sheet after every change and
 * published to sdmsChartStore, so the chart redraws as cells are typed, pasted, recalculated or
 * undone. Display only: nothing here is ever written to the workbook.
 *
 * `chartBuf` is the workbook that carries the chart part: the template itself in the designer,
 * and the TEMPLATE in Result Entry — the merged batch workbook is written by ExcelJS, which drops
 * charts. Rows above a mail-merge detail band (a calibration block) sit where the template has them.
 */
import { anchorToBox, readChartAnchors, readChartSeries, type SeriesRef } from './sdmsChartAnchor';
import { SdmsChartPreview } from './SdmsChartPreview';
import { onDropCharts, publishChart, type ChartPreviewData } from './sdmsChartStore';
import { readTemplateWorkbook } from '../toolbox/sdmsTemplateBuilder/templateWorkbook';

/** Loose facade surface (the presets build types it generically). */
type FApi = unknown;


const CHART_COMPONENT = 'sdms-workbook-chart';
/** How often a waiting chart checks whether its sheet is now the one shown. */
const ACTIVE_SHEET_POLL_MS = 400;
/** Univer's defaults when a sheet / column / row carries no size of its own. */
const DEFAULT_COL_PX = 88, DEFAULT_ROW_PX = 24;

interface ChartSourceSheet {
  name?: string;
  defaultColumnWidth?: number; defaultRowHeight?: number;
  columnData?: Record<string, { w?: number }>;
  rowData?: Record<string, { h?: number }>;
  cellData?: Record<number, Record<number, { v?: unknown; s?: unknown }>>;
}
interface ChartSourceData { sheetOrder?: string[]; sheets?: Record<string, ChartSourceSheet>; styles?: Record<string, { n?: { pattern?: string } } | null> }

/** Float every chart `chartBuf` holds over the live sheet `api` shows (`snapshot` = the IWorkbookData
 *  it was created from: column widths, row heights, styles). Ids start with `idPrefix`; call
 *  dropCharts(idPrefix) when the sheet is disposed. Best-effort, never throws. */
export async function floatWorkbookCharts(api: FApi, buf: ArrayBuffer, snapshot: unknown, idPrefix: string): Promise<void> {
  const data = snapshot as ChartSourceData;
  try {
    // Sheet names straight from the package: anchors count in WORKBOOK order (hidden sheets too), and the
    // editor's own list may not have arrived yet when the sheet first renders.
    const [anchors, series, info] = await Promise.all([readChartAnchors(buf), readChartSeries(buf), readTemplateWorkbook(buf)]);
    const workbookSheets = info.sheets;
    if (!anchors.length) return;
    const byName = new Map<string, ChartSourceSheet>();
    for (const id of data.sheetOrder ?? []) { const s = data.sheets?.[id]; if (s?.name) byName.set(s.name, s); }
    const fapi = api as unknown as {
      registerComponent?: (k: string, c: unknown) => unknown;
      getActiveWorkbook?: () => { getSheetByName?: (n: string) => {
        getRange?: (r: number, c: number, nr?: number, nc?: number) => unknown;
        addFloatDomToRange?: (range: unknown, layer: unknown, anchor: unknown, id?: string) => unknown;
      } | null } | null;
    };
    fapi.registerComponent?.(CHART_COMPONENT, SdmsChartPreview);
    const wb = fapi.getActiveWorkbook?.();

    const cellOf = (ref: SeriesRef | null, i: number) => {
      if (!ref) return undefined;
      const vertical = ref.endRow > ref.startRow;
      return byName.get(ref.sheet)?.cellData?.[ref.startRow + (vertical ? i : 0)]?.[ref.startCol + (vertical ? 0 : i)];
    };
    const numberOf = (v: unknown) => (typeof v === 'number' ? v : typeof v === 'string' && v.trim() !== '' && Number.isFinite(Number(v)) ? Number(v) : null);
    /** A series' values from the LIVE sheet (what the designer shows now, formulas computed), flattened. */
    const liveValues = (ref: SeriesRef | null): unknown[] => {
      if (!ref) return [];
      const ws = wb?.getSheetByName?.(ref.sheet) as { getRange?: (r: number, c: number, nr: number, nc: number) => { getValues?: () => unknown[][] } | null } | null | undefined;
      const values = ws?.getRange?.(ref.startRow, ref.startCol, ref.endRow - ref.startRow + 1, ref.endCol - ref.startCol + 1)?.getValues?.();
      return (values ?? []).flat();
    };
    /** Re-read and publish every chart's points. */
    const live: { id: string; base: Omit<ChartPreviewData, 'points'>; x: SeriesRef | null; y: SeriesRef | null }[] = [];
    const refresh = () => {
      for (const c of live) {
        const xs = liveValues(c.x), ys = liveValues(c.y);
        const points: ChartPreviewData['points'] = [];
        xs.forEach((xv, i) => { const x = numberOf(xv); if (x != null) points.push({ x, y: numberOf(ys[i]) }); });
        publishChart(c.id, { ...c.base, points });
      }
    };

    const skipped: string[] = [];
    let shown = 0;
    const pending = new Map<string, (() => boolean)[]>();
    for (const a of anchors) {
      const name = workbookSheets[a.sheetIndex];                // anchors use WORKBOOK order (hidden sheets too)
      const sheet = name ? byName.get(name) : undefined;
      const ws = name ? wb?.getSheetByName?.(name) : null;
      if (!name || !sheet || !ws?.getRange || !ws.addFloatDomToRange) {
        skipped.push(`${name ?? `sheet #${a.sheetIndex + 1}`}: ${!name ? 'no such sheet' : !sheet ? 'hidden / not loaded' : 'float DOM unavailable'}`);
        continue;
      }
      const colW = (c: number) => sheet.columnData?.[c]?.w ?? sheet.defaultColumnWidth ?? DEFAULT_COL_PX;
      const rowH = (r: number) => sheet.rowData?.[r]?.h ?? sheet.defaultRowHeight ?? DEFAULT_ROW_PX;
      const box = anchorToBox(a, colW, rowH);

      const s = series.get(name);
      const yStyle = cellOf(s?.y ?? null, 0)?.s;
      const yFormat = (typeof yStyle === 'string' ? data.styles?.[yStyle] : (yStyle as { n?: { pattern?: string } } | undefined))?.n?.pattern ?? null;
      const chartId = `${idPrefix}-${a.sheetIndex}-${a.fromRow}-${a.fromCol}`;
      live.push({ id: chartId, x: s?.x ?? null, y: s?.y ?? null,
        base: { yTitle: s?.yTitle ?? null, legend: s?.legend ?? true, markers: s?.markers ?? false, yFormat } });

      // A float DOM can only be added to the sheet being DRAWN (Univer has no render container for
      // the others), so each chart waits for its sheet to become the active one.
      const place = () => !!ws.addFloatDomToRange!(ws.getRange!(a.fromRow, a.fromCol, 1, 1),
        { componentKey: CHART_COMPONENT, data: { chartId }, allowTransform: false },
        { width: Math.max(40, box.width), height: Math.max(40, box.height), marginX: a.fromColOffPx, marginY: a.fromRowOffPx,
          horizonOffsetAlign: 'left', verticalOffsetAlign: 'top' },
        chartId);
      pending.set(name, [...(pending.get(name) ?? []), place]);
    }

    const placeOn = (sheetName: string | undefined) => {
      const list = sheetName ? pending.get(sheetName) : undefined;
      if (!sheetName || !list) return;
      pending.delete(sheetName);
      for (const place of list) {
        try { if (place()) shown++; else skipped.push(`${sheetName}: not placed`); }
        catch (e) { skipped.push(`${sheetName}: ${(e as Error).message}`); }
      }
      // eslint-disable-next-line no-console
      console.info(`[SDMS] charts shown: ${shown}/${anchors.length}`, skipped.length ? skipped : '');
    };
    const ev = api as unknown as {
      addEvent?: (name: string, cb: (p: { activeSheet?: { getSheetName?: () => string } }) => void) => unknown;
      Event?: { ActiveSheetChanged?: string; SheetValueChanged?: string };
    };
    // Live: re-read the plotted cells after any value change (one refresh per frame however many
    // cells a paste or fill touched). Formula results land through the same event.
    refresh();
    let queued = false;
    const disposeOf = (sub: unknown) => () => { (sub as { dispose?: () => void } | null)?.dispose?.(); };
    if (live.length && ev.addEvent && ev.Event?.SheetValueChanged)
      onDropCharts(idPrefix, disposeOf(ev.addEvent(ev.Event.SheetValueChanged, () => {
        if (queued) return;
        queued = true;
        requestAnimationFrame(() => { queued = false; refresh(); });
      })));

    // A chart waits for its sheet to be the one shown. The ActiveSheetChanged event is used where it
    // fires (the designer); SDMS Result Entry does not deliver it, so a light poll of the active sheet
    // backs it up — the same way that sheet already tracks its active tab — and stops once every chart
    // is placed or the sheet closes.
    const activeName = () =>
      (wb as unknown as { getActiveSheet?: () => { getSheetName?: () => string } | null } | null)?.getActiveSheet?.()?.getSheetName?.();
    if (pending.size && ev.addEvent && ev.Event?.ActiveSheetChanged)
      onDropCharts(idPrefix, disposeOf(ev.addEvent(ev.Event.ActiveSheetChanged, p => {
        const sheetName = p.activeSheet?.getSheetName?.();
        requestAnimationFrame(() => placeOn(sheetName));        // after the sheet's render container exists
      })));
    placeOn(activeName());
    if (pending.size) {
      const timer = setInterval(() => {
        if (!pending.size) { clearInterval(timer); return; }
        try { placeOn(activeName()); } catch { clearInterval(timer); }   // disposed under us
      }, ACTIVE_SHEET_POLL_MS);
      onDropCharts(idPrefix, () => clearInterval(timer));
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[SDMS] chart preview skipped', e);
  }
}

